import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Keyboard, X } from "@phosphor-icons/react";

/**
 * Global keyboard shortcuts:
 *   Cmd/Ctrl+K          → open command palette
 *   Cmd/Ctrl+/          → toggle AI Copilot drawer
 *   ?                   → open shortcuts cheatsheet
 *   G then I/P/D/S/V/W/B → go-to: Invoices/Projects/Dashboard/Service tickets/Vendor POs/Warehouses/Bills(purchases)
 *   N                   → new record on current list (dispatches custom event 'celsius:new')
 *
 * Ignores everything when focus is inside an input/textarea/contenteditable.
 */

const GOTO_MAP = {
  i: "/invoices",
  p: "/projects",
  d: "/dashboard",
  s: "/service/tickets",
  v: "/vendor-pos",
  w: "/warehouses",
  b: "/purchases",
  g: "/goods-receipts",
  y: "/inventory",
  m: "/material-requests",
};

export default function KeyboardShortcuts({ onOpenPalette, onOpenCopilot }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const pendingRef = useRef({ char: null, ts: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const inField = (el) => {
      if (!el) return false;
      const t = (el.tagName || "").toUpperCase();
      return t === "INPUT" || t === "TEXTAREA" || t === "SELECT" || el.isContentEditable;
    };

    const handler = (e) => {
      const cmd = e.metaKey || e.ctrlKey;

      // Cmd/Ctrl + K
      if (cmd && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenPalette?.();
        return;
      }
      // Cmd/Ctrl + /
      if (cmd && e.key === "/") {
        e.preventDefault();
        onOpenCopilot?.();
        return;
      }

      if (inField(e.target)) return;

      // Shortcuts that don't need cmd
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (e.key.toLowerCase() === "n") {
        // dispatch new-record event; each list page listens
        window.dispatchEvent(new CustomEvent("celsius:new"));
        return;
      }

      // G + letter sequence
      const now = Date.now();
      const key = e.key.toLowerCase();
      if (pendingRef.current.char === "g" && (now - pendingRef.current.ts) < 900) {
        const dest = GOTO_MAP[key];
        pendingRef.current = { char: null, ts: 0 };
        if (dest) { e.preventDefault(); navigate(dest); }
        return;
      }
      if (key === "g") {
        pendingRef.current = { char: "g", ts: now };
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpenPalette, onOpenCopilot, navigate]);

  return (
    <AnimatePresence>
      {helpOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[999] bg-black/40 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setHelpOpen(false)}
          data-testid="shortcuts-overlay"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
            className="w-[520px] max-w-[92vw] bg-white rounded-md shadow-2xl border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <Keyboard size={16} className="text-[#EA580C]" />
              <div className="flex-1 text-[13px] font-semibold text-slate-800">Keyboard shortcuts</div>
              <button onClick={() => setHelpOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </header>
            <div className="p-4 grid grid-cols-2 gap-3 text-[12.5px]">
              <Section title="Global">
                <Row keys={["⌘", "K"]} label="Command palette" />
                <Row keys={["⌘", "/"]} label="Toggle AI Copilot" />
                <Row keys={["?"]} label="This cheatsheet" />
              </Section>
              <Section title="Jump to">
                <Row keys={["G", "D"]} label="Dashboard" />
                <Row keys={["G", "I"]} label="Invoices" />
                <Row keys={["G", "P"]} label="Projects" />
                <Row keys={["G", "V"]} label="Vendor POs" />
                <Row keys={["G", "B"]} label="Purchases" />
                <Row keys={["G", "G"]} label="Goods receipts" />
                <Row keys={["G", "Y"]} label="Site inventory" />
                <Row keys={["G", "W"]} label="Warehouses" />
                <Row keys={["G", "M"]} label="Material requests" />
                <Row keys={["G", "S"]} label="Service tickets" />
              </Section>
              <Section title="On any list">
                <Row keys={["N"]} label="New record" />
              </Section>
              <Section title="Command palette">
                <Row keys={["?", "…"]} label="Ask AI a question" />
                <Row keys={["↑ ↓"]} label="Navigate results" />
                <Row keys={["↵"]} label="Open result" />
              </Section>
            </div>
            <footer className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
              Note: On macOS use ⌘ (Command). On Windows/Linux use Ctrl.
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ keys, label }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-700">{label}</span>
      <div className="flex items-center gap-1">
        {keys.map((k, i) => (
          <kbd key={`${label}-${k}-${i}`} className="bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5 text-[11px] font-mono text-slate-700 min-w-[22px] text-center">{k}</kbd>
        ))}
      </div>
    </div>
  );
}
